import { unCamelCase } from '@bagonboard/wiseman';
import { Dialog, DialogActions } from '@material-ui/core';
import Button from 'components/CustomButtons/Button';
import { getAmount as formatPrice } from 'modules/getAmount';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useOrder } from '#views/Dashboard/hooks';
import { calculateOrderInvoice } from '#utils';

import {
  InvoiceBags,
  InvoiceBagsQuantity,
  InvoiceDialogContent,
  InvoiceDialogTitle,
  InvoiceLine,
  InvoicePrice,
  InvoiceProductName,
  InvoiceProducts,
  InvoiceTotal,
  useInvoiceStyle,
} from './style';


const renderDetail = (detail) => {
  if (detail === 'First bag on generic service') {
    return 'First bag';
  }
  if (detail === 'Second bag on generic service') {
    return 'Additional bags';
  }
  return detail;
};

const renderProductDetail = ({
  _id, detail, price, quantity,
}) => (
  <InvoiceLine key={_id}>
    <InvoiceBags>
      {quantity > 1 && <InvoiceBagsQuantity>{quantity}</InvoiceBagsQuantity>}
      {renderDetail(detail)}
    </InvoiceBags>
    <InvoicePrice>
      {formatPrice(price * quantity)}
    </InvoicePrice>
  </InvoiceLine>
);

const renderProducts = products => products.map(({ _id, detail, detailProducts }) => (
  <Fragment key={_id}>
    <InvoiceProductName>
      {unCamelCase(detail)}
    </InvoiceProductName>
    {detailProducts.map(renderProductDetail)}
  </Fragment>
));

const renderTotals = totals => totals
  .filter(({ total }, index, { length }) => total || index === (length - 1))
  .map(({ name, total }, index, { length }) => (
    <InvoiceLine
      isTotal
      key={name}
      isLast={index === (length - 1)}
      isFirst={!index}
    >
      <InvoiceTotal>{name}</InvoiceTotal>
      <InvoicePrice>{formatPrice(total)}</InvoicePrice>
    </InvoiceLine>
  ));

const Invoice = ({ setOpen, open }) => {
  const { order } = useOrder();
  const {
    paymentIntentId,
    totals,
    products,
    hasInvoice,
  } = calculateOrderInvoice(order.data);

  const classes = useInvoiceStyle();

  return (
    <Dialog
      className={classes.root}
      open={open}
      aria-labelledby="invoice-dialog"
      onEscapeKeyDown={setOpen}
      onClick={setOpen}
    >
      <InvoiceDialogTitle id="invoice-dialog">Invoice</InvoiceDialogTitle>

      <InvoiceDialogContent>
        { hasInvoice
          ? (
            <>
              <InvoiceProducts>Products</InvoiceProducts>
              {renderProducts(products)}
              {renderTotals(totals)}
            </>
          )
          : (
            <h2>Invoice not found</h2>
          )
        }
      </InvoiceDialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button color="danger" onClick={setOpen} simple data-testid="close-btn">
          Close
        </Button>
        <Button
          data-testid="stripe-btn"
          color="info"
          disabled={!paymentIntentId}
          target="_blank"
          title={!paymentIntentId ? 'No Stripe payment found' : ''}
          className={!paymentIntentId ? 'invoice-button-stripe-disabled' : ''}
          href={paymentIntentId ? `https://dashboard.stripe.com/payments/${paymentIntentId}` : '#'}
          onClick={!paymentIntentId ? e => e.preventDefault() : null}
        >
          See Stripe
        </Button>
      </DialogActions>

    </Dialog>
  );
};

renderProductDetail.propTypes = {
  _id     : PropTypes.string.isRequired,
  price   : PropTypes.number.isRequired,
  detail  : PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
};

renderProducts.propTypes = PropTypes.arrayOf(PropTypes.shape({
  detail        : PropTypes.string.isRequired,
  _id           : PropTypes.string.isRequired,
  detailProducts: PropTypes.arrayOf(renderProductDetail).isRequired,
}));

renderTotals.propTypes = PropTypes.arrayOf(PropTypes.shape({
  name : PropTypes.string,
  total: PropTypes.number,
}));

Invoice.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open   : PropTypes.bool.isRequired,
};

export default Invoice;
